/**
 * odtFreeTextFallback.ts — M02
 *
 * Fallback de extracción de infraestructuras cuando la extracción tabular
 * devuelve 0 resultados. Escanea párrafos ODT fuera de tablas buscando
 * patrones de coordenadas UTM con contexto de nombre de infraestructura.
 *
 * CASOS DE USO:
 *   - Molvízar: ODT con 0 tablas de infraestructuras, coords en texto narrativo
 *   - ODTs básicos de municipios pequeños con listados en prosa
 *   - Documentos con coordenadas en secciones de descripción
 *
 * PIPELINE:
 *   extractFromODT() → tablas → 0 resultados → extractFromODTFreeText()
 *
 * @module lib/odtFreeTextFallback
 * @version 1.0.0
 */

import {
  extractCoordinatesFromFreeText,
  hasCoordinatesInText,
} from './freeTextCoordExtractor';
import type { ExtractedCoordinate } from './freeTextCoordExtractor';
import { normalizeSpanFragments } from './spanFragmentNormalizer';
import { deconcatenateText } from './textDeconcatenator';
import { classifyAddressType, detectInfrastructureType } from './documentExtractor';
import type { ExtractedInfrastructure, ExtractionStats } from '../types/processing';

// ============================================================================
// TIPOS
// ============================================================================

export interface FreeTextFallbackResult {
  infrastructures: ExtractedInfrastructure[];
  stats: ExtractionStats;
  /** Indica que se usó el fallback de texto libre */
  usedFreeTextFallback: true;
}

/** Bloque de texto con coordenadas y contexto */
interface TextBlock {
  /** Texto completo del bloque (párrafo o grupo de párrafos) */
  text: string;
  /** Coordenadas extraídas */
  coordinates: ExtractedCoordinate[];
  /** Nombre de infraestructura detectado (si hay) */
  inferredName: string;
  /** Dirección detectada (si hay) */
  inferredAddress: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Patrones para detectar nombres de infraestructura en contexto */
const INFRA_NAME_PATTERNS = [
  // "Centro de Salud", "Consultorio Médico", "Colegio Público", etc.
  /(?:Centro\s+de\s+Salud|Consultorio(?:\s+Médico)?|Hospital|Colegio(?:\s+Público)?|Instituto|CEIP|IES|Guardería|Parque\s+de\s+Bomberos|Cuartel(?:\s+de\s+la\s+Guardia\s+Civil)?|Comisaría|Polideportivo|Piscina(?:\s+Municipal)?|Campo\s+de\s+Fútbol|Pabellón|Ayuntamiento|Casa\s+Consistorial|Biblioteca|Centro\s+Cultural|Cementerio|Depósito(?:\s+de\s+agua)?|EDAR|ETAP|Depuradora|Subestación|Centro\s+de\s+Transformación|Gasolinera|Estación\s+de\s+servicio|Farmacia|Residencia|Centro\s+de\s+Día|Mercado|Plaza\s+de\s+Abastos|Iglesia|Ermita)[\s,.:;]?[^.;!\n]{0,60}/i,
];

/** Patrones de dirección en texto libre */
const ADDRESS_PATTERNS = [
  // "C/ Real, 12", "Calle Mayor nº 5", "Avda. de Andalucía"
  /(?:C\/|Calle|Avda\.?|Avenida|Plaza|Pza\.?|Camino|Ctra\.?|Carretera|Paseo|Ronda)\s+[A-ZÁÉÍÓÚÑ][^\n,;]{3,50}(?:\s*(?:n[ºo°]?\s*\d+|,?\s*\d+))?/i,
];

/** ID counter para fallback */
let fallbackIdCounter = 0;

function generateFallbackId(): string {
  fallbackIdCounter++;
  return `ft-${Date.now()}-${fallbackIdCounter}`;
}

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Extrae párrafos de un documento ODT XML que NO están dentro de tablas.
 * Agrupa párrafos cercanos en bloques lógicos.
 */
export function extractParagraphsOutsideTables(contentXml: string): string[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(contentXml, 'application/xml');

  const textNS = 'urn:oasis:names:tc:opendocument:xmlns:text:1.0';
  const tableNS = 'urn:oasis:names:tc:opendocument:xmlns:table:1.0';

  const allParagraphs = doc.getElementsByTagNameNS(textNS, 'p');
  const result: string[] = [];

  for (let i = 0; i < allParagraphs.length; i++) {
    const p = allParagraphs[i];

    // Verificar que el párrafo NO está dentro de una tabla
    let insideTable = false;
    let parent = p.parentElement;
    while (parent) {
      if (parent.namespaceURI === tableNS && parent.localName === 'table') {
        insideTable = true;
        break;
      }
      parent = parent.parentElement;
    }

    if (!insideTable) {
      const text = (p.textContent || '').trim();
      if (text.length > 5) {
        result.push(text);
      }
    }
  }

  return result;
}

/**
 * Intenta extraer el nombre de la infraestructura del contexto textual
 * alrededor de las coordenadas encontradas.
 */
function inferInfrastructureName(text: string): string {
  // Limpiar fragmentación de spans
  const clean = normalizeSpanFragments(text).corrected;

  for (const pattern of INFRA_NAME_PATTERNS) {
    const match = clean.match(pattern);
    if (match) {
      // Limpiar y truncar
      return deconcatenateText(match[0].trim().replace(/[,.:;]+$/, '')).corrected.substring(0, 150);
    }
  }

  // Si no hay match de infraestructura, usar el inicio del texto como nombre genérico
  const firstSentence = clean.split(/[.;!\n]/)[0]?.trim() || '';
  if (firstSentence.length > 10 && firstSentence.length < 120) {
    return deconcatenateText(firstSentence).corrected;
  }

  return 'Infraestructura (texto libre)';
}

/**
 * Intenta extraer una dirección del contexto textual.
 */
function inferAddress(text: string): string {
  const clean = normalizeSpanFragments(text).corrected;

  for (const pattern of ADDRESS_PATTERNS) {
    const match = clean.match(pattern);
    if (match) {
      return deconcatenateText(match[0].trim()).corrected.substring(0, 200);
    }
  }

  return '';
}

/**
 * Agrupa párrafos consecutivos con contexto para mejorar la detección.
 * Cada bloque contiene el párrafo con coordenadas + el párrafo anterior como contexto.
 */
function buildTextBlocks(paragraphs: string[]): TextBlock[] {
  const blocks: TextBlock[] = [];

  for (let i = 0; i < paragraphs.length; i++) {
    const para = paragraphs[i];

    if (!hasCoordinatesInText(para)) continue;

    // Crear bloque con contexto: párrafo anterior + actual
    const contextParagraph = i > 0 ? paragraphs[i - 1] : '';
    const fullText = contextParagraph ? `${contextParagraph} ${para}` : para;

    const extraction = extractCoordinatesFromFreeText(para, {
      includeLowConfidence: false,
      validateRanges: true,
    });

    if (extraction.coordinates.length === 0) continue;

    blocks.push({
      text: fullText,
      coordinates: extraction.coordinates,
      inferredName: inferInfrastructureName(fullText),
      inferredAddress: inferAddress(fullText),
    });
  }

  return blocks;
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Extrae infraestructuras de texto libre en un documento ODT.
 * Se usa como fallback cuando la extracción tabular devuelve 0 resultados.
 *
 * @param contentXml - XML del content.xml del ODT
 * @param municipality - Municipio detectado (para asignación)
 * @param province - Provincia detectada
 * @returns Resultado con infraestructuras extraídas de texto libre
 */
export function extractFromODTFreeText(
  contentXml: string,
  municipality?: string,
  province?: string,
): FreeTextFallbackResult {
  const paragraphs = extractParagraphsOutsideTables(contentXml);
  const blocks = buildTextBlocks(paragraphs);

  const infrastructures: ExtractedInfrastructure[] = [];
  const byTypology: Record<string, number> = {};
  let withCoordinates = 0;

  for (const block of blocks) {
    for (const coord of block.coordinates) {
      const nombre = block.inferredName;
      const direccion = block.inferredAddress;
      const tipo = detectInfrastructureType(nombre, '', 'Texto libre');
      const addressType = classifyAddressType(direccion, nombre);

      const infra: ExtractedInfrastructure = {
        id: generateFallbackId(),
        tabla: 'Texto libre (fallback)',
        nombre,
        tipo,
        direccion,
        xOriginal: String(coord.x),
        yOriginal: String(coord.y),
        hasCoordinates: true,
        status: 'original',
        municipio: municipality,
        provincia: province,
        addressType,
      };

      infrastructures.push(infra);
      withCoordinates++;
      byTypology[tipo] = (byTypology[tipo] || 0) + 1;
    }
  }

  return {
    infrastructures,
    stats: {
      total: infrastructures.length,
      withCoordinates,
      withoutCoordinates: 0,
      byTypology,
      byTable: { 'Texto libre': infrastructures.length },
    },
    usedFreeTextFallback: true,
  };
}
